import { useMemo } from "react";
import { useAppUser } from "../context/AppUserContext";
// CB:BEGIN single-readiness-payload
// Backend GET /api/contractors/self returns unified readiness; AppUserProvider fetches once.
// This hook now derives view state from that shared payload to avoid flicker and duplication.
// CB:END single-readiness-payload

export type VerificationState = "approved" | "pending" | "not_submitted";

type ContractorsSelfResponse = {
  contractor?: {
    id?: string;
    stripeAccountId?: string | null;
    bidCount?: number;
    [k: string]: unknown;
  };
  verification?: {
    status?: string;
    missing?: string[];
    canSubmit?: boolean;
    submittedAt?: string | null;
    rejectionReason?: string | null;
    expires?: { licenseExpiresAt?: string | null; generalLiabilityExpiresAt?: string | null; workersCompExpiresAt?: string | null };
    isVerified?: boolean;
    isComplete?: boolean;
  };
  agreement?: {
    accepted?: boolean;
    version?: number;
    acceptedAt?: string | null;
    signedPdf?: { docId?: string; storageKey?: string; bucket?: string } | null;
  };
  stripe?: {
    connected?: boolean;
    payoutReady?: boolean;
    accountId?: string | null;
  };
  payment?: {
    hasValidPayment?: boolean;
    paymentMethodType?: string | null;
  };
  canBid?: boolean;
  blockReason?: string | null;
};

export function useContractorReadiness() {
  const {
    role,
    contractorsSelf,
    contractorsLoading,
    contractorsError,
    loading: appLoading,
    error: appError,
  } = useAppUser();

  // Only contractors have readiness; others see default "not ready" with loading false.
  const isContractor = role === 'CONTRACTOR';

  const loading = isContractor ? contractorsLoading || appLoading : appLoading;
  const baseError = contractorsError || appError;
  const errorKind: 'network' | 'auth' | 'not_found' | 'server' | null = baseError ? 'server' : null;

  const selfJson = (isContractor ? contractorsSelf : null) as (ContractorsSelfResponse & { verificationState?: VerificationState }) | null;

  const stripeConnected = !!selfJson?.stripe?.connected;
  const stripePayoutReady = !!selfJson?.stripe?.payoutReady;
  const stripeAccountId = selfJson?.stripe?.accountId ?? selfJson?.contractor?.stripeAccountId ?? null;
  const status = selfJson?.verification?.status ?? '';

  let verificationState: VerificationState = 'not_submitted';
  if (selfJson?.verificationState) {
    verificationState = selfJson.verificationState;
  } else if (status === 'APPROVED' || selfJson?.verification?.isVerified) {
    verificationState = 'approved';
  } else if (status === 'SUBMITTED') {
    verificationState = 'pending';
  }

  const bidCount = selfJson?.contractor?.bidCount ?? 0;
  const isFirstBid = bidCount === 0;
  const hasValidPayment = !!selfJson?.payment?.hasValidPayment;
  const hasStripeAccountId = !!selfJson?.contractor?.stripeAccountId;
  const verificationStatusRaw = status || null;
  const rejectionReason = selfJson?.verification?.rejectionReason ?? null;
  const backendCanBid = selfJson?.canBid ?? false;
  const backendBlockReason = selfJson?.blockReason ?? null;

  return useMemo(
    () => {
      // Backend is source of truth: canBid and blockReason come from GET /api/contractors/self.
      const canSubmitBid = backendCanBid;
      // Show "Ready to bid" when verification is approved (so admin-approved contractors see it),
      // or when backend says they can actually bid (verification + Stripe + agreement).
      const accountReady = verificationState === 'approved' || backendCanBid;

      return {
        loading,
        error: baseError,
        errorKind,
        stripeConnected,
        stripePayoutReady,
        stripeAccountId,
        verificationState,
        verificationStatusRaw,
        rejectionReason,
        hasValidPayment,
        hasStripeAccountId,
        bidCount,
        isFirstBid,
        accountReady,
        canSubmitBid,
        blockReason: backendBlockReason,
        shouldShowPaymentSetupCard: !isFirstBid && !stripeConnected,
        shouldShowPendingVerificationBanner: verificationState === 'pending',
        shouldShowApprovedVerificationBanner: verificationState === 'approved',
      };
    },
    [
      loading,
      baseError,
      errorKind,
      stripeConnected,
      stripePayoutReady,
      stripeAccountId,
      verificationState,
      verificationStatusRaw,
      rejectionReason,
      hasValidPayment,
      hasStripeAccountId,
      bidCount,
      isFirstBid,
      backendCanBid,
      backendBlockReason,
    ]
  );
}

